/** Typ eines einzelnen Changelog-Eintrags */
export type ChangelogEntryType = 'feature' | 'fix' | 'improvement';

/** Ein einzelner Changelog-Eintrag */
export interface ChangelogEntry {
  type: ChangelogEntryType;
  description: string;
}

/** Eine komplette Version mit ihren Einträgen */
export interface ChangelogVersion {
  version: string;
  date: string;
  title: string;
  entries: ChangelogEntry[];
}

/**
 * Aktuelle App-Version.
 * Muss mit CHANGELOG[0].version übereinstimmen.
 * Bei einem neuen Release: Version hochsetzen + neuen Eintrag am Anfang des Arrays einfügen.
 */
export const APP_VERSION = '1.11.2';

/**
 * Changelog-Daten – neueste Version zuerst.
 * Das Modal zeigt standardmässig nur den neuesten Eintrag.
 */
export const CHANGELOG: ChangelogVersion[] = [
  {
    version: '1.11.2',
    date: '2026-10-08',
    title: 'Gemini-Anfrage für neue Modellgeneration vorbereitet',
    entries: [
      {
        type: 'improvement',
        description:
          'PDF-Erkennung mit Gemini: Die Anfrage an Google wird an die neue Modellgeneration angepasst (Gemini 3 und neuer nutzen «thinking_level» statt «thinking_budget», ohne eigene Sampling-Parameter). Für die bisher eingesetzten Modelle (Gemini 2.x) bleibt die Anfrage unverändert.',
      },
    ],
  },
  {
    version: '1.11.1',
    date: '2026-10-07',
    title: 'Lieferort und Zeitfenster-Ende in Mailvorlagen, neue Ladeanimation',
    entries: [
      {
        type: 'fix',
        description:
          'Bestehende Benachrichtigungsvorlagen zeigen nach der Umstellung den Lieferort statt der Rechnungsadresse und das Ende des Zeitfensters. Die Umstellung erfolgt gesammelt pro Mandant; individuell formulierte Vorlagen bleiben erkennbar und können im Vorlagen-Editor nachgelesen werden.',
      },
      {
        type: 'improvement',
        description:
          'Die Ladeanimation zeigt jetzt das Welti-Furrer-Logo mit sanftem Pulsieren. Sie erscheint erst nach einer kurzen Verzögerung, damit bei schnellen Ladezeiten nichts flackert, und respektiert die Systemeinstellung «Animationen reduzieren».',
      },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-09-30',
    title: 'Sichere Berechtigungen, verlässliche Sperrzeiten, Buchungsseite in drei Sprachen',
    entries: [
      {
        type: 'feature',
        description:
          'Die Buchungsseite ist in Deutsch, Französisch und Italienisch verfügbar, mit Sprachwahl für den Kunden. Bestätigungen, Erinnerungen, Umbuchungs- und Stornomails werden in der Sprache des Kunden versendet.',
      },
      {
        type: 'feature',
        description:
          'Adressvorschläge für die Lieferadresse auf der Buchungsseite: Strasse tippen, Vorschlag wählen, PLZ, Ort und Kanton werden ausgefüllt. Die Funktion kann pro Mandant aktiviert werden; manuelle Eingabe bleibt möglich.',
      },
      {
        type: 'fix',
        description:
          'Sperrzeiten (bisher «Blackout-Perioden») wirken sofort auch auf bereits erzeugte Zeitfenster und werden in der richtigen Zeitzone geprüft. Beim Anlegen wird angezeigt, wie viele Zeitfenster gesperrt wurden und ob bereits gebuchte Termine betroffen sind.',
      },
      {
        type: 'fix',
        description:
          'Die Umbuchung zeigt die Zeitfenster des Lieferorts statt der Rechnungsadresse. Der bisherige Termin wird beim Umbuchen sichtbar gesperrt statt still blockiert, und ein Kapazitätskonflikt wird verständlich gemeldet.',
      },
      {
        type: 'fix',
        description:
          'Kundenmails, Kalender, Termin-Tab und Dashboard zeigen den Lieferort statt der Rechnungsadresse; die Rechnungsadresse ist überall ausdrücklich beschriftet. Mehrere aktive Regeln je Gebiet erzeugen gemeinsam Zeitfenster, und die Buchungsfrist der massgebenden Regel wird angewendet.',
      },
      {
        type: 'fix',
        description:
          'Der Kundenflow behält Eingaben, Termin und Schritt bei abgelaufener Sitzung und verlängert die Sitzung automatisch. Stornierte oder bereits übermittelte Aufträge zeigen eine Statusseite mit Kontaktangaben statt eines nie absendbaren Formulars.',
      },
      {
        type: 'improvement',
        description:
          'Buchungsseite: Kanton als Auswahl, verständliche Feldfehler mit Sprung zum betroffenen Feld, Zeitfenster erst bei vollständiger PLZ, aktueller Termin bei einem Umbuchungslink, Kalenderdatei und Druckansicht auf der Erfolgsseite, Browser-Zurück wechselt zwischen den Schritten.',
      },
      {
        type: 'fix',
        description:
          'Aufträge ohne gültige Kundenmail (z. B. aus dem PDF-Import) werden als «Kontakt fehlt» markiert und nicht per E-Mail eingeladen; der Erinnerungsversand überspringt sie. PDF-Dokumente ohne Kundenname oder Adresse gehen in die manuelle Prüfung.',
      },
      {
        type: 'feature',
        description:
          'Global Admins sehen in der Auftragsliste den Mandanten je Auftrag, können danach filtern und sehen den Mandanten im Auftragsdetail.',
      },
      {
        type: 'improvement',
        description:
          'Bestätigte Aufträge lassen sich bei Kontakt, Notizen und Angaben vor Ort weiter bearbeiten; die Adressfelder sind mit Hinweis gesperrt. Vor «Bestätigen», «Erledigt», «Partner entfernen», «Partner deaktivieren» und «Benutzer sperren» erscheint ein Bestätigungsdialog.',
      },
      {
        type: 'improvement',
        description:
          'Filter, Sortierung und Seite der Auftragsliste bleiben in der URL erhalten und lassen sich teilen; die Listen auf dem Dashboard sind direkt verlinkt. Ungespeicherte Änderungen im Bearbeiten-Dialog und in der PDF-Prüfung werden vor dem Verlassen geschützt.',
      },
      {
        type: 'feature',
        description:
          'PDF-Prüfung: Dokumente können mit Grund abgelehnt und mit dem bereits bestehenden Auftrag verknüpft werden; der Link zum bestehenden Auftrag wird angezeigt.',
      },
      {
        type: 'fix',
        description:
          'Benutzerverwaltung: Global-Admin-Konten können nur durch Global Admins angelegt oder geändert werden; die eigene Rolle und das eigene Konto lassen sich nicht selbst deaktivieren. Die 2FA-Deaktivierung verlangt das Passwort, ein Telefonwechsel erfordert eine neue Verifikation.',
      },
      {
        type: 'improvement',
        description:
          'Einstellungen zeigen für Geheimnisse nur noch «Konfiguriert»/«Nicht konfiguriert». Speicherfehler bei Regeln und Sperrzeiten erscheinen direkt im offenen Dialog; Lieferschein-Downloads melden Fehler und funktionieren auch mit Popup-Blocker.',
      },
      {
        type: 'improvement',
        description:
          'Sicherheit: Webhook- und Dienst-URLs werden vor jedem Aufruf geprüft (nur https, keine internen Adressen), Webhooks sind zusätzlich signiert, Logos werden bereinigt, CSV-Exporte gegen Formeleinschleusung geschützt, und sicherheitsrelevante Änderungen (Einstellungen, Vorlagen, Passwörter, 2FA, Rollen) werden im Audit-Protokoll festgehalten.',
      },
      {
        type: 'improvement',
        description:
          'Einheitliche Schweizer Schreibweise, deutsche Statusbezeichnungen, beschriftete Icon-Schaltflächen, einheitliche Rückmeldungen als Snackbar und «Storniert durch …» als Stornogrund.',
      },
    ],
  },
  {
    version: '1.10.1',
    date: '2026-09-16',
    title: 'Zuverlässigere Buchung und Disposition',
    entries: [
      {
        type: 'fix',
        description:
          'Benutzer- und Mandantensperren greifen auch für bestehende Anmeldungen. Passwortänderungen entwerten offene Zwei-Faktor-Anmeldungen. Beim Benutzerwechsel werden vorherige Kundendaten vollständig entfernt.',
      },
      {
        type: 'fix',
        description:
          'Gleichzeitige Buchungen und Slotsperren werden zuverlässig berücksichtigt. Nach einem Terminkonflikt bleiben Kundeneingaben erhalten und ein anderer Termin kann gewählt werden.',
      },
      {
        type: 'fix',
        description:
          'Der Kalender lädt den angezeigten Zeitraum, ordnet Termine dem richtigen Tag zu und zeigt gleichzeitig beginnende Termine getrennt an.',
      },
      {
        type: 'fix',
        description:
          'Lieferadresse, Terminart und Angaben vor Ort bleiben beim Buchen und Bearbeiten erhalten. Die regionale Terminwahl richtet sich nach dem tatsächlichen Lieferort.',
      },
      {
        type: 'fix',
        description:
          'PDFs und gleichnamige E-Mail-Anhänge werden unabhängig gespeichert. Unterbrochene PDF-Verarbeitungen können nach Ablauf der Verarbeitungssperre erneut gestartet werden.',
      },
      {
        type: 'fix',
        description:
          'Versandfehler werden korrekt angezeigt, Erinnerungen wieder zuverlässig geprüft und zusätzliche Outlook-Termine bei unbestätigter Absage verhindert. Fehlende Outlook-Termine können bei einer erneuten bestätigten Umbuchung wiederhergestellt werden.',
      },
      {
        type: 'fix',
        description:
          'Erfolgreich gespeicherte Änderungen bleiben auch bei nachfolgenden Webhookfehlern erfolgreich. Buchungsbestätigungen zeigen bei gestörtem Nachladen die übermittelten Angaben; ein ungeklärter Partner-Informationsstand wird neutral angezeigt.',
      },
      {
        type: 'fix',
        description:
          'Offene Aufträge erscheinen ohne leere Zwischenseiten. Ungespeicherte Eingaben werden beim Seitenwechsel geschützt und während des Speicherns weiterbearbeitete Einstellungen bleiben erhalten.',
      },
      {
        type: 'fix',
        description:
          'Partnerwechsel aktualisieren den Lieferschein. Ausstehende Terminmitteilungen sind erkennbar und Umbuchungshistorie sowie Standardmail entsprechen dem tatsächlichen Terminstatus.',
      },
      {
        type: 'improvement',
        description:
          'Termine lassen sich mit der Tastatur auswählen, Seitenaktionen bleiben auf kleinen Displays erreichbar und eine Buchungsvorlaufzeit von null Stunden wird korrekt angezeigt.',
      },
      {
        type: 'improvement',
        description:
          'Die Auftragsübersicht zeigt Termine mit Datum, Zeitfenster und Heute-/Morgen-Hinweis direkt neben dem Status. Termine lassen sich auch mobil sortieren; die E-Mail steht platzsparend beim Kunden',
      },
      {
        type: 'fix',
        description:
          'Tagesrouten bleiben nach erfolgloser Adresssuche aufrufbar; längere Touren öffnen sich in passenden Google-Maps-Abschnitten auch im mobilen Browser',
      },
      {
        type: 'fix',
        description:
          'Adressänderungen beim Erfassen, Importieren und Buchen verwerfen veraltete Koordinaten, damit die Route zur aktuellen Lieferadresse führt',
      },
      {
        type: 'fix',
        description:
          'Automatische Aktualisierungen und Adressvorschläge erhalten eigene Abfragekontingente pro Benutzer; Suchfehler werden im Adressfeld angezeigt',
      },
      {
        type: 'fix',
        description:
          'Der Versandbutton für Subunternehmer aktualisiert sich nach Auftragsänderungen und Terminbestätigungen',
      },
      {
        type: 'fix',
        description:
          'Ungültige Positionsmengen werden auch beim Import abgewiesen; Mengen unter 0,01 werden nicht mehr auf null gerundet',
      },
      {
        type: 'fix',
        description:
          'Tagesrouten kennzeichnen unbekannte Distanzen und weisen auf Stopps hin, die im Google-Maps-Link fehlen',
      },
      {
        type: 'fix',
        description:
          'Die Einstellung «Adress-Autocomplete» wird jetzt gespeichert; Adressvorschläge erscheinen damit in den Auftragsformularen, sobald die Funktion aktiviert ist',
      },
      {
        type: 'fix',
        description:
          'Seiten und bereits besuchte Auftragslisten laden beim Wechsel automatisch aktuelle Daten; Dashboard-Zahlen werden nach Auftragsänderungen zuverlässig aktualisiert',
      },
      {
        type: 'fix',
        description:
          'Gespeicherte Mandanteneinstellungen wirken sofort auf die übrigen Seiten; automatische Aktualisierungen erhalten ungespeicherte Formulareingaben',
      },
      {
        type: 'fix',
        description:
          'Bei bestätigten Lieferterminen erscheint die Warnung «Outlook-Termin fehlt» nur bei aktivierter Outlook-Synchronisierung',
      },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-09-10',
    title: 'Adress-Autocomplete & Routenoptimierung',
    entries: [
      {
        type: 'feature',
        description:
          'Adress-Vorschläge beim Erfassen von Aufträgen über geo.admin.ch (Einstellungen → Buchung → Auftragserfassung)',
      },
      {
        type: 'feature',
        description:
          'Tagesroute berechnet die Reihenfolge nach Koordinaten (Luftlinie) und öffnet die Route direkt in Google Maps',
      },
      {
        type: 'improvement',
        description:
          'Subunternehmer-Karte zeigt klar, ob der Auftrag bereits versendet wurde, und nennt den Grund, wenn Versand noch nicht möglich ist',
      },
      {
        type: 'improvement',
        description: 'Auftragsliste, Auftragsdetail und Tagesroute aktualisieren sich automatisch im Minutentakt',
      },
      {
        type: 'fix',
        description: 'Positionen-Editor: Eingabefelder springen bei Hinweistexten nicht mehr',
      },
      {
        type: 'fix',
        description: 'Tagesroute: Kilometer-Total über alle Zeitfenster korrekt, Google-Maps-Route startet am Depot',
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-09-10',
    title: 'Auftragspositionen',
    entries: [
      {
        type: 'feature',
        description:
          'Positionen (Anzahl, Einheit, Bezeichnung, Artikel-Nr., Artikel/Dienstleistung) können beim Erstellen und Bearbeiten eines Auftrags erfasst werden (Einstellungen → Buchung → «Positionen im Auftrag erfassen»)',
      },
      {
        type: 'feature',
        description:
          'Lieferschein zeigt Positionen nummeriert als Pos. / Anzahl / Bezeichnung mit Leerzeilen für handschriftliche Ergänzungen',
      },
      {
        type: 'improvement',
        description:
          'PDF-Import übernimmt erkannte Positionen strukturiert statt als Textblock in der Beschreibung (nur bei aktivierter Positionen-Erfassung)',
      },
      {
        type: 'improvement',
        description:
          'Positionen erscheinen in der Auftragsansicht, auf der Kunden-Buchungsseite und stehen der Subunternehmer-Mail als {{items}} zur Verfügung',
      },
      {
        type: 'improvement',
        description:
          'Einstellung «Positionen im Auftrag erfassen» wurde von Subunternehmer-Tab nach Buchungseinstellungen verschoben',
      },
      {
        type: 'improvement',
        description: 'Dienstleistungs-Positionen werden ohne Einheit und mit Suffix «(Dienstleistung)» angezeigt',
      },
      {
        type: 'improvement',
        description: 'Positionsindikator-Icon in der Auftragsliste zeigt Anzahl der Positionen',
      },
      {
        type: 'improvement',
        description:
          'Positionen: Hinweis im Auftrag, wenn der Subunternehmer bereits informiert wurde, mit erneuter Benachrichtigung',
      },
      {
        type: 'improvement',
        description: 'Positionen: Mobile Ansicht des Editors als Karten statt Tabelle',
      },
      {
        type: 'fix',
        description: 'Menge bei Positionen muss > 0 sein (0 wird nicht mehr akzeptiert)',
      },
      {
        type: 'fix',
        description:
          'Positionen: Audit-Protokoll zeigt IDs neuer Positionen korrekt, Editor mit klareren Eingabehinweisen',
      },
    ],
  },
  {
    version: '1.8.1',
    date: '2026-09-09',
    title: 'Lieferschein: Design & Aktualität',
    entries: [
      {
        type: 'fix',
        description:
          'Lieferschein verwendet die Mandantenfarbe jetzt korrekt (3-stellige Hex-Kurzformen wurden falsch gerendert); Farbeingabe im Branding ist neu 6-stellig',
      },
      {
        type: 'fix',
        description:
          'Lieferschein-Download zeigt das Mandantenlogo auch bei Direkt-Rendering; nicht darstellbare Logos (z.B. SVG) werden protokolliert statt still ausgelassen',
      },
      {
        type: 'improvement',
        description: 'Footer und Seitenzahl auf jeder Lieferschein-Seite, Datum in der Mandanten-Zeitzone',
      },
      {
        type: 'improvement',
        description:
          'Gespeicherter Lieferschein wird bei Auftragsänderung, Terminverschiebung oder Kundenbuchung verworfen und beim nächsten Abruf neu erzeugt',
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-09-09',
    title: 'Extraktionsprofil pro Mandant',
    entries: [
      {
        type: 'feature',
        description:
          'PDF-Extraktion pro Mandant konfigurierbar: Dokumenttypen, Absender-Blacklist, Feldbezeichnungen und KI-Hinweise (Einstellungen → PDF-OCR → Extraktionsprofil)',
      },
      {
        type: 'feature',
        description: 'Prompt-Vorschau zeigt vor dem Speichern, welche Anweisungen die KI erhält',
      },
      {
        type: 'improvement',
        description:
          'Ohne eigenes Profil bleibt das bisherige Verhalten unverändert (Standardprofil); Prompt- und Profilversion werden pro verarbeitetem PDF protokolliert',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-09-08',
    title: 'Subunternehmer, Lieferschein & Tagesroute',
    entries: [
      {
        type: 'feature',
        description: 'Subunternehmer-Verwaltung in den Einstellungen (Stammdaten, Kantone, PLZ-Zuordnung)',
      },
      {
        type: 'feature',
        description: 'Automatische Zuweisung von Subunternehmern bei Auftragsbestätigung',
      },
      {
        type: 'feature',
        description: 'Lieferschein-PDF zum Download und automatischem Versand',
      },
      {
        type: 'feature',
        description: 'Tagesroute mit optimierter Stopp-Reihenfolge nach Zeitfenster und PLZ-Nähe',
      },
      {
        type: 'improvement',
        description: 'Subunternehmer-Formular: optionale Felder gekennzeichnet, Prioritäts-Hinweis korrigiert',
      },
      {
        type: 'fix',
        description: 'Slot-Buchung zählte doppelt (Kapazität wurde bei jeder Buchung um 2 statt 1 reduziert)',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-09-02',
    title: 'PIN-Schutz, Terminverschiebung & Datenkonsistenz',
    entries: [
      {
        type: 'fix',
        description:
          'PIN-geschützte Einladungen zeigen Auftragsdaten und das Original-PDF erst nach Eingabe der PIN. Vorher genügte der Link allein.',
      },
      {
        type: 'fix',
        description:
          'Umbuchungs-Benachrichtigungen erzeugen keinen PIN-losen Buchungslink mehr; ein neuer Link übernimmt den PIN-Schutz der ursprünglichen Einladung.',
      },
      {
        type: 'fix',
        description:
          'Nach einer Stornierung durch Mitarbeitende werden offene Einladungslinks ungültig – der Kunde kann den stornierten Auftrag nicht mehr über den alten Link buchen.',
      },
      {
        type: 'feature',
        description:
          'Bestätigte Termine können jetzt direkt umgebucht werden (inkl. Aktualisierung des Outlook-Termins), ohne den Auftrag zu stornieren.',
      },
      {
        type: 'feature',
        description:
          'Aufträge „In Prüfung“ können eine neue Einladung erhalten, damit der Kunde erneut einen Termin wählen kann.',
      },
      {
        type: 'feature',
        description:
          'Administratoren können versehentlich abgeschlossene Aufträge wieder öffnen („Wieder öffnen“ auf der Auftragsseite).',
      },
      {
        type: 'fix',
        description:
          'Auftragsliste, Dashboard und Kalender aktualisieren sich jetzt sofort nach Bestätigen, Umbuchen, Stornieren oder Abschliessen eines Auftrags (vorher bis zu 5 Minuten veraltet).',
      },
      {
        type: 'improvement',
        description:
          'Beim Wechsel zurück in den Browser-Tab werden Daten automatisch nachgeladen – hilfreich, wenn mehrere Personen parallel disponieren.',
      },
      {
        type: 'fix',
        description:
          'Verfügbarkeitsregeln: „Max. Slots pro Tag“ gilt nur noch für die jeweilige Regel (PLZ, Kanton oder Standard) und kappt nicht mehr die Standardverfügbarkeit anderer Orte.',
      },
      {
        type: 'fix',
        description:
          'Webhook-Ereignis „order.rescheduled“ wird jetzt tatsächlich versendet (wurde bisher als unbekannt verworfen).',
      },
    ],
  },
  {
    version: '1.5.2',
    date: '2026-08-26',
    title: 'Security-Kit Integration',
    entries: [
      {
        type: 'improvement',
        description: 'Pre-Commit-Hooks, Ruff-Linting und Security-Scanning für den gesamten Backend-Code integriert.',
      },
      {
        type: 'fix',
        description: 'SQLite-Kompatibilität bei DB-Pool-Einstellungen behoben (betrifft lokale Entwicklung und CI).',
      },
    ],
  },
  {
    version: '1.5.1',
    date: '2026-06-24',
    title: 'Kalender-Integration Verbesserungen',
    entries: [
      {
        type: 'improvement',
        description: 'Kalendertermin wird bei Umbuchung automatisch im Outlook aktualisiert.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-06-16',
    title: 'Security Audit Fixes, Stornierungs-Toggle & Performance',
    entries: [
      {
        type: 'improvement',
        description:
          'Session wird nach Passwortänderung automatisch invalidiert – alle bestehenden Tokens werden ungültig (SEC-F01).',
      },
      {
        type: 'improvement',
        description:
          'Account-Lockout: Nach 10 fehlgeschlagenen Login-Versuchen wird das Konto für 15 Minuten gesperrt (SEC-F02).',
      },
      {
        type: 'improvement',
        description: 'Passwort-Blockliste auf 1662 häufige Passwörter erweitert, Notfall-Fallback gehärtet (SEC-F03).',
      },
      {
        type: 'improvement',
        description:
          'Eager Loading für Auftrags- und Benutzerlisten – weniger Datenbankabfragen, schnellere Ladezeiten (PERF-F01).',
      },
      {
        type: 'feature',
        description:
          'Stornierungs-E-Mails können pro Mandant aktiviert/deaktiviert werden (neuer Toggle in den E-Mail-Einstellungen).',
      },
      {
        type: 'fix',
        description: 'Kalendertab-Emoji entfernt für konsistentes UI.',
      },
      {
        type: 'improvement',
        description:
          'Dashboard: Stornierte und erledigte Aufträge werden im Board «Aktuelle Aufträge» nicht mehr angezeigt.',
      },
    ],
  },
  {
    version: '1.4.1',
    date: '2026-06-16',
    title: 'Bugfixes: Kalendertermin',
    entries: [
      {
        type: 'fix',
        description:
          'CC-Empfänger aus E-Mail-Vorlagen werden jetzt auch als Teilnehmer im Outlook-Kalendertermin hinzugefügt.',
      },
      {
        type: 'fix',
        description:
          'Kalendertermin-Stornierung funktioniert jetzt zuverlässig (Fehler bei leerer Graph-API-Antwort behoben).',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-06-16',
    title: 'Terminvorlagen, CC-Adressen & Reschedule',
    entries: [
      {
        type: 'feature',
        description:
          'Sachbearbeiter können Termine umbuchen und Kunden über den neuen Termin benachrichtigen (Reschedule).',
      },
      {
        type: 'feature',
        description: 'CC-Empfänger pro E-Mail-Vorlage konfigurierbar (max. 5 Adressen).',
      },
      {
        type: 'feature',
        description: 'Kalendertermin-Vorlage (calendar_body) kann jetzt direkt im UI bearbeitet werden.',
      },
      {
        type: 'feature',
        description: 'Neue Vorlage «Terminänderung» (reschedule) für Kunden-Benachrichtigung bei Umbuchung.',
      },
      {
        type: 'improvement',
        description: 'Kalendertermin-Betreff zeigt jetzt nur noch die Auftragsnummer.',
      },
      {
        type: 'fix',
        description: 'Umbuchungs-Dialog zeigt jetzt Termine für 8 Wochen statt nur 3 Wochen.',
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-06-11',
    title: 'Einladungs-Erinnerung & Konfiguration',
    entries: [
      {
        type: 'feature',
        description: 'Automatische Erinnerung per E-Mail, wenn eine Terminbuchung nach 72 Stunden noch aussteht.',
      },
      {
        type: 'feature',
        description: 'Der Erinnerungs-Zeitraum kann in den Buchungseinstellungen individuell konfiguriert werden.',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-06-10',
    title: 'Juni-Update: Bugfixes, Pflichtfelder & Kalenderfarben',
    entries: [
      {
        type: 'fix',
        description: 'Das Tenant-Logo wird jetzt korrekt auf der öffentlichen Buchungsseite angezeigt.',
      },
      {
        type: 'fix',
        description: 'PLZ-Verfügbarkeitsregeln haben jetzt immer Vorrang vor Kanton-Regeln.',
      },
      {
        type: 'improvement',
        description: 'Name und Telefon des Kontakts vor Ort sind jetzt Pflichtfelder im Buchungsformular.',
      },
      {
        type: 'fix',
        description:
          'Keine doppelten E-Mails mehr, wenn Auftraggeber und Kontakt vor Ort die gleiche E-Mail-Adresse haben.',
      },
      {
        type: 'feature',
        description: 'Erledigte Aufträge werden im Kalender grün dargestellt, mit Legende.',
      },
      {
        type: 'feature',
        description:
          'Erledigte und stornierte Aufträge werden in der Auftragsliste standardmässig ausgeblendet. Über eine Checkbox können sie eingeblendet werden.',
      },
      {
        type: 'improvement',
        description:
          '„Garage vorhanden" durch „Mitarbeiter-Anmeldung erforderlich" ersetzt (neues Feld, DB-Migration nötig)',
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-05-27',
    title: 'Mai-Update: Neue Funktionen & Verbesserungen',
    entries: [
      {
        type: 'feature',
        description: 'Neuer HTML-Editor für E-Mail-Vorlagen ermöglicht eine flexiblere Gestaltung.',
      },
      {
        type: 'feature',
        description: 'Agenten können nun PDFs verarbeiten und Aufträge erstellen.',
      },
      {
        type: 'feature',
        description: 'Neuer Status-Indikator im Footer zeigt die aktuelle Systemversion an.',
      },
      {
        type: 'fix',
        description: 'Übersichtlichere Darstellung von Postleitzahlen in den Tabellen.',
      },
      {
        type: 'fix',
        description: 'Verschiedene Fehlerbehebungen bei der PDF-Verarbeitung und beim Logout.',
      },
      {
        type: 'improvement',
        description: 'Allgemeine Design-Verbesserungen und Systemaktualisierungen für mehr Stabilität.',
      },
      {
        type: 'fix',
        description:
          "Bugfix: 'PDF-Details anzeigen' Button auf der Auftragsdetailseite navigiert nun korrekt zur PDF-Detailansicht.",
      },
    ],
  },
];
